import React from 'react';
import Iconify from 'src/components/Iconify';
import { calcularTotais, formatCurrency, formatDataExtenso, resolverExpiracao, resolverVariavel, substituirVariaveisHtml } from './canvasUtils';

function calcularItem(item) {
  const bruto = (Number(item.quantidade) || 0) * (Number(item.preco_unitario) || 0);
  return bruto - (bruto * (Number(item.desconto_percent) || 0) / 100);
}

function justifyFromAlign(align) {
  return align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
}

// Tabela de itens do orçamento renderizada dentro do canvas (sempre leitura —
// os valores são editados pelos dados da proposta, não pelo elemento).
function ItensTableCanvas({ proposta }) {
  const itens = proposta?.itens || [];
  const { subtotal, total } = calcularTotais(itens, proposta?.desconto_global_percent);
  return (
    <div className="w-full text-[13px] text-gray-700">
      <div className="grid grid-cols-[1fr_60px_100px_60px_100px] gap-2 pb-1.5 border-b border-gray-200 text-[10px] font-bold uppercase tracking-wider text-gray-400">
        <span>Produto ou serviço</span>
        <span className="text-right">Qtd.</span>
        <span className="text-right">Preço</span>
        <span className="text-right">Desc.</span>
        <span className="text-right">Total</span>
      </div>
      {itens.map((item, i) => (
        <div key={i} className="grid grid-cols-[1fr_60px_100px_60px_100px] gap-2 py-1.5 border-b border-gray-100">
          <span className="truncate">{item.descricao || 'Item sem descrição'}</span>
          <span className="text-right">{item.quantidade}</span>
          <span className="text-right">{formatCurrency(item.preco_unitario)}</span>
          <span className="text-right">{item.desconto_percent || 0}%</span>
          <span className="text-right font-medium text-gray-900">{formatCurrency(calcularItem(item))}</span>
        </div>
      ))}
      <div className="flex justify-end mt-2">
        <div className="w-[260px] space-y-1">
          <div className="flex justify-between text-gray-500 text-xs">
            <span>Subtotal</span><span>{formatCurrency(subtotal)}</span>
          </div>
          {Boolean(Number(proposta?.desconto_global_percent)) && (
            <div className="flex justify-between text-gray-500 text-xs">
              <span>Desconto geral</span><span>{proposta.desconto_global_percent}%</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-gray-900 text-sm pt-1 border-t border-gray-200">
            <span>Valor total</span><span>{formatCurrency(total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Renderiza o conteúdo de um elemento do canvas (sem posicionamento — o wrapper cuida disso).
// `resolver` = substituir variáveis por dados reais (preview/leitura); no editor mostra o placeholder.
export default function CanvasElementContent({ elemento, proposta, resolver = false }) {
  const { tipo, estilo = {}, conteudo = {} } = elemento;

  if (tipo === 'texto') {
    const html = resolver ? substituirVariaveisHtml(conteudo.html, proposta) : (conteudo.html || '');
    return (
      <div
        className="w-full h-full overflow-hidden break-words [&_p]:m-0"
        style={{
          fontSize: estilo.fontSize || 14,
          color: estilo.color || '#374151',
          textAlign: estilo.textAlign || 'left',
          fontWeight: estilo.fontWeight || 'normal',
          fontStyle: estilo.fontStyle || 'normal',
          lineHeight: estilo.lineHeight || 1.6,
          background: estilo.background || 'transparent',
          borderRadius: estilo.borderRadius || 0,
          opacity: estilo.opacity ?? 1,
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  if (tipo === 'variavel') {
    const valor = resolver ? resolverVariavel(conteudo.chave, proposta) : null;
    const mostrarChip = !resolver || valor === undefined || valor === null || valor === '';
    return (
      <div
        className="w-full h-full flex items-center overflow-hidden"
        style={{
          fontSize: estilo.fontSize || 14,
          color: estilo.color || '#111827',
          fontWeight: estilo.fontWeight || 'normal',
          justifyContent: estilo.textAlign === 'center' ? 'center' : estilo.textAlign === 'right' ? 'flex-end' : 'flex-start',
          opacity: estilo.opacity ?? 1,
        }}
      >
        {mostrarChip ? (
          <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-violet-100 text-violet-700 whitespace-nowrap">
            {`{{${conteudo.chave || 'variavel'}}}`}
          </span>
        ) : (
          <span className="truncate">{String(valor)}</span>
        )}
      </div>
    );
  }

  if (tipo === 'imagem') {
    if (!conteudo.url) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center gap-1 border border-dashed border-gray-300 rounded-lg bg-gray-50 text-gray-400">
          <Iconify icon="mdi:image-outline" width={22} />
          <span className="text-[10px]">Imagem</span>
        </div>
      );
    }
    return (
      <img
        src={conteudo.url}
        alt=""
        draggable={false}
        className="w-full h-full pointer-events-none select-none"
        style={{
          objectFit: estilo.objectFit || 'contain',
          borderRadius: estilo.borderRadius || 0,
          opacity: estilo.opacity ?? 1,
        }}
      />
    );
  }

  if (tipo === 'retangulo' || tipo === 'linha') {
    return (
      <div
        className="w-full h-full"
        style={{
          background: estilo.background || '#F3E8FF',
          border: (estilo.borderWidth || 0) > 0 ? `${estilo.borderWidth}px solid ${estilo.borderColor || '#7C3AED'}` : 'none',
          borderRadius: estilo.borderRadius || 0,
          opacity: estilo.opacity ?? 1,
        }}
      />
    );
  }

  if (tipo === 'itens') {
    return (
      <div className="w-full h-full overflow-hidden" style={{ opacity: estilo.opacity ?? 1 }}>
        <ItensTableCanvas proposta={proposta} />
      </div>
    );
  }

  // --- Blocos dinâmicos pré-configurados (data, validade, dados do cliente) ---
  // Sempre calculados a partir da proposta — sem edição de texto livre.

  if (tipo === 'data') {
    const cidade = conteudo.cidade || '';
    return (
      <div
        className="w-full h-full flex items-center overflow-hidden whitespace-nowrap"
        style={{
          fontSize: estilo.fontSize || 13,
          color: estilo.color || '#6b7280',
          fontWeight: estilo.fontWeight || 'normal',
          justifyContent: justifyFromAlign(estilo.textAlign),
          opacity: estilo.opacity ?? 1,
        }}
      >
        {cidade ? `${cidade}, ` : ''}{formatDataExtenso(proposta?.created_at)}.
      </div>
    );
  }

  if (tipo === 'validade') {
    const expira = resolverExpiracao(proposta);
    return (
      <div
        className="w-full h-full flex items-center overflow-hidden whitespace-nowrap"
        style={{
          fontSize: estilo.fontSize || 13,
          color: estilo.color || '#374151',
          fontWeight: estilo.fontWeight || 'normal',
          justifyContent: justifyFromAlign(estilo.textAlign),
          opacity: estilo.opacity ?? 1,
        }}
      >
        <strong className="mr-1">Validade:</strong>proposta válida até {formatDataExtenso(expira)}.
      </div>
    );
  }

  if (tipo === 'dados_cliente') {
    const contato = proposta?.contato || {};
    const extra = [contato.email, contato.telefone].filter(Boolean).join(' · ');
    return (
      <div
        className="w-full h-full overflow-hidden"
        style={{ textAlign: estilo.textAlign || 'left', opacity: estilo.opacity ?? 1 }}
      >
        <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-0.5">Em atenção à</p>
        <p className="truncate" style={{ fontSize: estilo.fontSize || 13, color: estilo.color || '#374151', fontWeight: estilo.fontWeight || 500 }}>
          {contato.nome || '-'}
        </p>
        {extra && <p className="text-xs text-gray-500 truncate">{extra}</p>}
      </div>
    );
  }

  return null;
}
